'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import styles from './GiftCardCodeInput.module.css';

interface GiftCardCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onError: (error: string | null) => void;
  onFocus?: () => void;
  disabled?: boolean;
}

export const GiftCardCodeInput: React.FC<GiftCardCodeInputProps> = ({
  value,
  onChange,
  onError,
  onFocus,
  disabled = false,
}) => {
  const t = useTranslations('homepage');
  const [inputValues, setInputValues] = useState<string[]>(['', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Placeholder text for each input part
  const placeholderParts = ['A1B2', 'C3E4', 'F5G6', 'H7I8'];

  // Initialize input values from the passed value
  useEffect(() => {
    if (value) {
      const cleanValue = value.replace(/-/g, '');
      const parts = cleanValue.match(/.{1,4}/g) || [];
      const paddedParts = [...parts, '', '', '', ''].slice(0, 4);
      setInputValues(paddedParts);
    }
  }, [value]);

  // Update the parent component when input values change
  useEffect(() => {
    const fullCode = inputValues.join('');
    onChange(fullCode);

    // Clear error when user starts typing
    if (fullCode.length > 0) {
      onError(null);
    }
  }, [inputValues, onChange, onError]);

  const handleInputChange = (index: number, inputValue: string) => {
    // Only allow alphanumeric characters and limit to 4 characters
    const sanitizedValue = inputValue
      .replace(/[^A-Z0-9]/gi, '')
      .toUpperCase()
      .slice(0, 4);

    const newInputValues = [...inputValues];
    newInputValues[index] = sanitizedValue;
    setInputValues(newInputValues);

    // Auto-focus next input if current input is full
    if (sanitizedValue.length === 4 && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    // Handle backspace to move to previous input
    if (e.key === 'Backspace' && inputValues[index] === '' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle arrow keys for navigation
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData('text')
      .replace(/[^A-Z0-9]/gi, '')
      .toUpperCase();

    if (pastedData.length > 0) {
      const parts = pastedData.match(/.{1,4}/g) || [];
      const paddedParts = [...parts, '', '', '', ''].slice(0, 4);
      setInputValues(paddedParts);

      // Focus the next empty input or the last input
      const nextEmptyIndex = paddedParts.findIndex((part) => part.length < 4);
      const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 3;
      inputRefs.current[focusIndex]?.focus();
    }
  };

  return (
    <div className={styles.inputContainer}>
      <div
        className={styles.inputGroup}
        role="group"
        aria-label={t('homepage.giftCardCodeInput.aria.codeInput')}
      >
        {inputValues.map((inputValue, index) => (
          <React.Fragment key={index}>
            <input
              className={styles.codeInput}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              value={inputValue}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={onFocus}
              placeholder={placeholderParts[index]}
              disabled={disabled}
              maxLength={4}
              aria-label={`Gift card code part ${index + 1} of 4`}
              aria-describedby="gift-card-code-help"
              autoComplete="off"
              spellCheck="false"
            />
            {index < 3 && (
              <span
                className={styles.hyphen}
                aria-hidden="true"
                role="presentation"
              >
                -
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
      <div id="gift-card-code-help" className={styles.srOnly}>
        {t('homepage.giftCardCodeInput.instructions.codeHelp')}
      </div>
    </div>
  );
};
